/* SolicitorPortalInvitesPanel.jsx — Steward-only Deal Room panel.
 *
 * Lists the deal's solicitor participants and lets the steward mint
 * a calm magic-link portal invite per participant. Mounted in the
 * right intelligence rail of the Deal Room. Auto-hides if there are
 * no solicitor participants on the deal.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ShieldCheck, Mail, Loader2, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../data/authStore";

const STEWARD_ROLES = new Set(["admin", "director", "manager"]);

const cardStyle = {
  borderRadius: "16px",
  border: "1px solid var(--gp-card-border, #E2E8F0)",
  background: "var(--gp-card-bg, #FFFFFF)",
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const SOLICITOR_TYPES = new Set(["buyer_solicitor", "seller_solicitor"]);
const PARTICIPANT_LABEL = {
  buyer_solicitor:  "Buyer's solicitor",
  seller_solicitor: "Seller's solicitor",
};

const formatExpiry = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "short",
    });
  } catch { return ""; }
};

export function SolicitorPortalInvitesPanel({ dealId }) {
  const { user } = useAuth();
  const isSteward = useMemo(
    () => STEWARD_ROLES.has(((user?.role || "") + "").toLowerCase()),
    [user],
  );
  const [participants, setParticipants] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const refetch = useCallback(async () => {
    if (!dealId || !isSteward) {
      setLoading(false);
      return;
    }
    try {
      const [pResp, iResp] = await Promise.all([
        api.get(`/api/v2/deals/${dealId}/participants`),
        api.get(`/api/solicitor-portal/deals/${dealId}/invites`),
      ]);
      const pList = pResp.data?.items || pResp.data || [];
      setParticipants(
        (Array.isArray(pList) ? pList : []).filter(
          (p) => SOLICITOR_TYPES.has(p.participant_type) && p.email,
        ),
      );
      setInvites(iResp.data?.items || []);
    } catch {
      setParticipants([]);
      setInvites([]);
    } finally {
      setLoading(false);
    }
  }, [dealId, isSteward]);

  useEffect(() => {
    setLoading(true);
    refetch();
  }, [refetch]);

  const inviteByParticipant = useMemo(() => {
    const map = {};
    for (const i of invites) {
      if (i.revoked) continue;
      if (!map[i.participant_id]) map[i.participant_id] = i;
    }
    return map;
  }, [invites]);

  const sendInvite = async (p) => {
    if (busyId) return;
    setBusyId(p.id);
    try {
      const res = await api.post(
        `/api/solicitor-portal/deals/${dealId}/invite`,
        { participant_id: p.id },
      );
      const body = res.data || {};
      if (body.ok) {
        if (body.delivered) {
          toast.success(`Portal link emailed to ${p.full_name || p.email}`);
        } else {
          toast.success("Portal link minted (email not configured)");
        }
        // Copy link to clipboard quietly so the steward can share manually
        if (body.portal_url) {
          try { await navigator.clipboard.writeText(body.portal_url); } catch { /* noop */ }
        }
        await refetch();
      } else {
        toast.error("Could not mint portal link");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mint portal link");
    } finally {
      setBusyId(null);
    }
  };

  const revokeInvite = async (invite) => {
    if (busyId) return;
    setBusyId(invite.id);
    try {
      await api.post(`/api/solicitor-portal/invites/${invite.id}/revoke`);
      toast.success("Portal access revoked");
      await refetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not revoke access");
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = async (invite) => {
    if (!invite?.portal_url && !invite?.id) return;
    // We don't surface the raw token to the steward; instead refresh by minting.
    toast.message(
      "For security, a fresh link is needed. Send a new invite to share.",
    );
    setCopiedId(invite.id);
    setTimeout(() => setCopiedId(null), 1800);
  };

  if (!isSteward) return null;
  if (loading) return null;
  if (participants.length === 0) return null;

  return (
    <section
      className="p-4"
      style={cardStyle}
      data-testid="solicitor-portal-invites-panel"
    >
      <div className="flex items-center gap-1.5 mb-1">
        <ShieldCheck size={11} strokeWidth={2.3} className="text-violet-600" />
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700">
          Solicitor portal
        </p>
      </div>
      <h3 className="text-[13px] font-semibold text-slate-900 leading-tight">
        Magic-link coordination
      </h3>
      <p className="text-[11.5px] text-slate-500 leading-snug mt-0.5">
        One-tap calm access. No signup. Scoped to this transaction. Revocable.
      </p>

      <ul className="mt-3 space-y-3">
        {participants.map((p) => {
          const invite = inviteByParticipant[p.id];
          const isBusy = busyId === p.id || busyId === invite?.id;
          return (
            <li
              key={p.id}
              className="border-t first:border-t-0 pt-3 first:pt-0 border-slate-100"
              data-testid={`solicitor-portal-row-${p.id}`}
            >
              <p className="text-[12.5px] font-semibold text-slate-900 truncate">
                {p.full_name || p.email}
              </p>
              <p className="text-[11px] text-slate-500 truncate">
                {PARTICIPANT_LABEL[p.participant_type] || p.participant_type}
                {" · "}{p.email}
              </p>
              {invite ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span
                    className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-1 rounded-full"
                    style={{
                      background: "#F0FDF4",
                      border: "1px solid #DCFCE7",
                      color: "#047857",
                    }}
                    data-testid={`solicitor-portal-status-${p.id}`}
                  >
                    <CheckCircle2 size={10} />
                    Invited
                    {invite.use_count > 0 ? ` · ${invite.use_count} visits` : null}
                  </span>
                  {invite.expires_at ? (
                    <span className="text-[10.5px] text-slate-400">
                      Expires {formatExpiry(invite.expires_at)}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => sendInvite(p)}
                    disabled={isBusy}
                    className="h-7 px-2 rounded-md text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                    style={{
                      border: "1px solid rgba(139,92,246,0.28)",
                      color: "#5B21B6",
                      background:
                        "linear-gradient(135deg, rgba(139,92,246,0.06), rgba(99,102,241,0.03))",
                    }}
                    data-testid={`solicitor-portal-resend-${p.id}`}
                  >
                    {busyId === p.id ? (
                      <Loader2 size={10} className="animate-spin" />
                    ) : (
                      <Mail size={10} />
                    )}
                    Resend link
                  </button>
                  <button
                    type="button"
                    onClick={() => revokeInvite(invite)}
                    disabled={isBusy}
                    className="h-7 px-2 rounded-md text-[11px] font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                    data-testid={`solicitor-portal-revoke-${p.id}`}
                  >
                    Revoke
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => sendInvite(p)}
                  disabled={isBusy}
                  className="mt-2 h-9 px-3 rounded-lg text-[12px] font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-60"
                  style={{
                    background:
                      "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
                    boxShadow:
                      "0 8px 18px -10px rgba(99,102,241,0.55)",
                  }}
                  data-testid={`solicitor-portal-send-${p.id}`}
                >
                  {busyId === p.id ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Mail size={12} />
                  )}
                  Send portal link
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default SolicitorPortalInvitesPanel;
